import { API_BASE_URL } from '../config';
import { hashPassword } from '../utils/passwordHash';

export interface User {
  id: string;
  email: string;
  nickname: string;
  avatar?: string;
  created_at?: number;
  tech_stacks?: string[];
  resumeName?: string | null;
}

class AuthManager {
  private accessToken: string | null = null;
  private user: User | null = null;
  private listeners: Set<(isAuthenticated: boolean) => void> = new Set();
  private initPromise: Promise<boolean> | null = null;

  constructor() {
    // 从 LocalStorage 恢复 Token
    this.loadTokenFromStorage();
  }

  // 订阅认证状态变化
  subscribe(listener: (isAuthenticated: boolean) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // 通知订阅者
  private notifyListeners() {
    const isAuthenticated = this.isAuthenticated();
    this.listeners.forEach((listener) => listener(isAuthenticated));
  }

  // 从 LocalStorage 加载 Token
  private loadTokenFromStorage(): void {
    try {
      const data = localStorage.getItem('access_token');
      if (data) {
        const { token } = JSON.parse(data);
        this.accessToken = token;
      }
    } catch {
      // 解析失败，忽略
    }
  }

  // 保存 Token 到 LocalStorage
  private saveTokenToStorage(token: string): void {
    const data = {
      token,
      expires: Date.now() + 15 * 60 * 1000, // 15分钟
    };
    localStorage.setItem('access_token', JSON.stringify(data));
  }

  // 清除存储的 Token
  private clearTokenFromStorage(): void {
    localStorage.removeItem('access_token');
  }

  // 获取 Access Token
  getToken(): string | null {
    return this.accessToken;
  }

  // 获取当前用户
  getUser(): User | null {
    return this.user;
  }

  // 是否已认证
  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  // 检查昵称是否可用
  async checkNickname(
    nickname: string,
  ): Promise<{ available: boolean; message?: string }> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/auth/check-nickname?nickname=${encodeURIComponent(
          nickname,
        )}`,
      );
      const data = await response.json();

      if (response.status === 409) {
        return { available: false, message: data.error || '该昵称已被使用' };
      }

      return { available: true };
    } catch (error) {
      return { available: true }; // 网络错误时允许继续
    }
  }

  // 注册
  async register(
    email: string,
    password: string,
    nickname?: string,
  ): Promise<{ success: boolean; message: string; user?: User }> {
    try {
      // 对密码进行客户端哈希处理
      const hashedPassword = await hashPassword(password);
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password: hashedPassword,
          nickname,
          platform: 'pc',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, message: data.error || '注册失败' };
      }

      this.accessToken = data.accessToken;
      this.saveTokenToStorage(data.accessToken);

      // 注册成功后获取用户信息
      const user = await this.fetchUser();
      this.notifyListeners();

      return { success: true, message: '注册成功', user: user || undefined };
    } catch (error) {
      return { success: false, message: '网络错误，请稍后重试' };
    }
  }

  // 登录
  async login(
    email: string,
    password: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      // 对密码进行客户端哈希处理
      const hashedPassword = await hashPassword(password);
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // 携带 Cookie
        body: JSON.stringify({
          email,
          password: hashedPassword,
          platform: 'pc',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, message: data.error || '登录失败' };
      }

      this.accessToken = data.accessToken;
      this.user = data.user;
      this.saveTokenToStorage(data.accessToken);
      this.notifyListeners();

      return { success: true, message: '登录成功' };
    } catch (error) {
      return { success: false, message: '网络错误，请稍后重试' };
    }
  }

  // 刷新 Token
  async refresh(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // 携带 Refresh Token Cookie
      });

      if (!response.ok) {
        this.logout();
        return false;
      }

      const data = await response.json();
      this.accessToken = data.accessToken;
      this.saveTokenToStorage(data.accessToken);
      return true;
    } catch (error) {
      this.logout();
      return false;
    }
  }

  // 登出
  async logout(): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // 忽略错误
    }

    this.accessToken = null;
    this.user = null;
    this.clearTokenFromStorage();
    this.notifyListeners();

    // 触发登出事件，通知应用清理状态并跳转
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }

  // 获取用户信息
  async fetchUser(): Promise<User | null> {
    if (!this.accessToken) return null;

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/user`, {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      });

      if (response.status === 401) {
        // Token 过期，尝试刷新
        const refreshed = await this.refresh();
        if (refreshed) {
          return this.fetchUser();
        }
        // 刷新失败，清除本地令牌并触发登出
        await this.logout();
        return null;
      }

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      // 解析 tech_stacks JSON 字符串
      if (data.user && data.user.tech_stacks) {
        try {
          data.user.tech_stacks = JSON.parse(data.user.tech_stacks);
        } catch {
          data.user.tech_stacks = [];
        }
      }
      this.user = data.user;
      this.notifyListeners();
      return data.user;
    } catch (error) {
      return null;
    }
  }

  // 初始化（页面加载时调用）
  async init(): Promise<boolean> {
    // 如果正在初始化中，返回现有的 Promise
    if (this.initPromise) {
      return this.initPromise;
    }

    // 如果没有 access token，直接返回 false，不走获取用户信息流程
    if (!this.accessToken) {
      return false;
    }

    // 如果已经有用户信息，直接返回 true，不再重新获取
    if (this.user) {
      return true;
    }

    // 创建初始化 Promise
    this.initPromise = (async () => {
      try {
        // 验证 Token 是否有效
        const user = await this.fetchUser();
        if (user) {
          return true;
        }

        // 尝试用 Refresh Token 获取新的 Access Token
        return await this.refresh();
      } finally {
        // 重置 Promise，允许下次重新初始化
        this.initPromise = null;
      }
    })();

    return this.initPromise;
  }
}

export const auth = new AuthManager();

// 带认证的 API 请求封装
export async function authFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const token = auth.getToken();

  const headers = {
    ...options.headers,
    Authorization: token ? `Bearer ${token}` : '',
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Token 过期，尝试刷新
  if (response.status === 401) {
    const refreshed = await auth.refresh();
    if (refreshed) {
      // 重试请求
      return authFetch(url, options);
    }
  }

  return response;
}
